import { CoinGeckoClient, type CoinMarketChartResponse } from 'coingecko-api-v3'
import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import CoinPriceCacheBackendAPI from '@/networks/api/cacheCoinPrice/CoinPriceCacheBackendAPI'
import { CoinGeckoCoinPrice, type Maybe, type BaseCoinPrice } from '@/types'
import { getExecuteOnce } from '@/utils'

export const useCoinPriceAPIStore = defineStore('coinPriceAPI', () => {
  const client = new CoinGeckoClient()
  const cachedCoinPriceClient = CoinPriceCacheBackendAPI.getInstance()
  const coinPriceList = ref<BaseCoinPrice[]>([])
  const chart7d: Ref<TypeChart7dIdStruct[]> = ref([])
  const notFoundIds = new Set<string>()

  const getCoinPrice = (id: string): Maybe<BaseCoinPrice> => {
    if (!id) return
    const result = coinPriceList.value.find((val) => val.id === id)
    if (!result && !notFoundIds.has(id)) {
      notFoundIds.add(id)
    }
    return result
  }
  const addCoinPrice = (coinPrice: BaseCoinPrice) => {
    if (!coinPrice) return
    coinPriceList.value.push(coinPrice)
  }

  const fetchCoinPrice = getExecuteOnce(
    async ({
      force = false,
      coinGeckoIds,
      currency,
    }: {
      force?: boolean
      coinGeckoIds: string[]
      currency: string
    }): Promise<void> => {
      let filteredCoinGeckoIds: string[] = []
      if (!force) {
        filteredCoinGeckoIds = coinGeckoIds.filter((id) => {
          return !getCoinPrice(id)
        })
      } else {
        filteredCoinGeckoIds = [...coinGeckoIds]
      }
      if (!force && filteredCoinGeckoIds.length === 0) return
      try {
        const [cgMarkets, globalRes] = await Promise.all([
          cachedCoinPriceClient.coin.coinMarket({
            ids: filteredCoinGeckoIds.join(','),
            vs_currency: currency,
            sparkline: true,
          }),
          cachedCoinPriceClient.global.global(),
        ])

        for (const cgMarket of cgMarkets) {
          const coinPrice = new CoinGeckoCoinPrice(cgMarket, globalRes)
          addCoinPrice(coinPrice)
        }
      } catch (e) {
        // 에러를 다시 throw하여 상위에서 타임아웃 처리 가능하게 함
        throw e
      }
    },
  )

  type TypeChart7dIdStruct = {
    id: string
    data: CoinMarketChartResponse
  }
  const getChart7d = (id: string): Maybe<CoinMarketChartResponse> => {
    return chart7d.value.find((val) => val.id === id)?.data
  }
  const addChart7d = (id: string, chart: CoinMarketChartResponse) => {
    chart7d.value.push({
      id,
      data: chart,
    })
  }
  const fetchChart7d = getExecuteOnce(
    async ({
      force = false,
      coinGeckoId,
      currency,
    }: {
      force?: boolean
      coinGeckoId: string
      currency: string
    }): Promise<void> => {
      if (force || !getChart7d(coinGeckoId)) {
        const chart7d = await client.coinIdMarketChart({
          id: coinGeckoId,
          vs_currency: currency,
          days: 7,
        })
        addChart7d(coinGeckoId, chart7d)
      }
    },
  )

  const reset = () => {
    coinPriceList.value = []
    chart7d.value = []
  }

  return {
    coinPriceList,
    fetchCoinPrice,
    getCoinPrice,
    addCoinPrice,
    chart7d,
    getChart7d,
    fetchChart7d,
    reset,
  }
})
